import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, useToast } from '@morressier/alembic';
import { zodResolver } from '@hookform/resolvers/zod';
import { inviteReviewerSchema } from '@publishing-engine/domain/validation';
import { IReviewerInvitationQueue } from '@publishing-engine/domain/models';
import { AxiosError } from 'axios';
import { Overlay } from 'src/components/Overlay';
import { pluralizeString } from 'src/utils/string';
import ConfirmInviteModal from '../InviteReviewer/ConfirmInviteModal';
import ConfirmInviteExitModal from '../InviteReviewer/ConfirmInviteExitModal';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { ensureUser, fetchEnqueuedReviewers } from 'src/api/queries';
import {
  useActivateReviewerQueue,
  useReviewRequestQueue
} from 'src/api/commands';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { IBlockedReviewer } from '@publishing-engine/domain/views';
import { useSubmissionContextData } from '../../../../contexts/SubmissionContextProvider';
import AddReviewerWithSearch from '../ReviewerSearch/AddReviewerWithSearch';
import { useReviewerInviteList } from '../../../../contexts/ReviewerInviteListProvider';
import { Box, Flex } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import NavbarDashboard from 'src/components/NavbarDashboard';

interface Props {
  closeModal: () => void;
  submissionId: string;
  blockedReviewers?: IBlockedReviewer[];
}

type Reviewer = {
  email: string;
};

export type ReviewerForm = {
  reviewers: Reviewer[];
};

const AddReviewerToQueueModal = ({
  closeModal,
  submissionId,
  blockedReviewers = []
}: Props) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showExitConfirmModal, setShowExitConfirmModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const toast = useToast();
  const submissionContextId = useCurrentContextId() ?? '';
  const { organizationId } = useSubmissionData();

  const { data: reviewerInvitations } = useQuery({
    queryKey: [
      'submission-context',
      submissionContextId,
      submissionId,
      'reviewer-invitations'
    ],
    queryFn: () =>
      submissionContextId
        ? fetchEnqueuedReviewers(submissionContextId, submissionId)
        : undefined
  });

  const enqueuedReviewersKey = [
    'submission-context',
    submissionContextId,
    submissionId,
    'reviewer-invitations'
  ];
  const reviewsQueryKey = [
    'submission-context',
    submissionContextId,
    submissionId,
    'reviews'
  ];
  const reviewersKey = ['reviewer-list', { submissionContextId, submissionId }];
  const reviewListKey = ['review-list', { submissionContextId, submissionId }];

  const inviteQueueMutation = useReviewRequestQueue(
    submissionId,
    submissionContextId,
    enqueuedReviewersKey,
    organizationId,
    reviewerInvitations?.autoInvite
  );
  const activateReviewerQueueMutation = useActivateReviewerQueue(
    submissionId,
    submissionContextId,
    [reviewersKey, enqueuedReviewersKey, reviewsQueryKey, reviewListKey],
    organizationId
  );

  const { submissionContext } = useSubmissionContextData();
  const isReviewerAccountCreationEnabled =
    submissionContext?.config.cms?.lexicon?.reviewerActionCard?.createAccount ??
    false;

  const reviewerMethods = useForm<ReviewerForm>({
    defaultValues: {
      reviewers: []
    },
    resolver: zodResolver(inviteReviewerSchema)
  });
  const {
    formState: { errors, isDirty },
    reset
  } = reviewerMethods;

  const { reviewerList, selectedReviewersToInvite, markReviewersInvited } =
    useReviewerInviteList();
  const confirmedReviewers = reviewerList.filter(
    r => selectedReviewersToInvite[r.email] && !r.invited
  );

  const handleInviteQueue = async (reviewerEmails: string[]) => {
    const payload: IReviewerInvitationQueue[] = reviewerEmails.map(email => ({
      email,
      attachments: [],
      cc_recipient: []
    }));

    try {
      await Promise.all(reviewerEmails.map(email => ensureUser(email)));
    } catch (error) {
      let description = 'We are unable to invite these reviewers at this time.';
      if (error instanceof AxiosError && !!error?.response?.data?.message) {
        description = error?.response?.data?.message;
      }
      toast({
        variant: 'error',
        title: 'Invites Failed',
        description,
        isClosable: true
      });
      return false;
    }

    const response = await inviteQueueMutation.mutateAsync(payload);
    if (!response.isSuccess) {
      toast({
        variant: 'error',
        title: 'Error while adding Reviewers to Queue',
        isClosable: true
      });
    } else if (reviewerInvitations?.autoInvite) {
      await activateReviewerQueueMutation.mutateAsync(true);
    }

    return response.isSuccess;
  };

  const handleInviteReviewers = async () => {
    if (errors.reviewers?.length || isProcessing) return;
    setIsProcessing(true);

    const addToQueueReviewers = confirmedReviewers.map(r => r.email);

    let noOfReviewersInQueue = 0;

    if (addToQueueReviewers.length) {
      const isQueueInviteSuccess = await handleInviteQueue(addToQueueReviewers);
      noOfReviewersInQueue = isQueueInviteSuccess
        ? addToQueueReviewers.length
        : 0;
    }

    if (noOfReviewersInQueue > 0) {
      toast({
        variant: 'success',
        title: `${noOfReviewersInQueue} ${pluralizeString(
          'Reviewer',
          noOfReviewersInQueue
        )} Added In Queue`,
        description: `You can reorder the queue, or advance Reviewers by selecting 'Invite Now' from the context menu.`,
        isClosable: true
      });
      // Mark reviewers as queued in the sidebar so they show the badge
      // and stay on the search page (same behaviour as the invite flow)
      markReviewersInvited(addToQueueReviewers);
    }

    setIsProcessing(false);
    setShowConfirmModal(false);
  };

  const handleCloseModal = () => {
    if (isDirty) setShowExitConfirmModal(true);
    else {
      reset();
      closeModal();
    }
  };

  const handleExit = () => {
    setShowExitConfirmModal(false);
    closeModal();
    reset();
  };

  return (
    <Overlay>
      <FormProvider {...reviewerMethods}>
        {showConfirmModal && (
          <ConfirmInviteModal
            isProcessing={isProcessing}
            onClose={() => setShowConfirmModal(false)}
            onInviteReviewers={handleInviteReviewers}
            option={'queue'}
          />
        )}
        {showExitConfirmModal && (
          <ConfirmInviteExitModal
            onClose={handleExit}
            onCancel={() => setShowExitConfirmModal(false)}
          />
        )}
      </FormProvider>
      <Overlay>
        <NavbarDashboard />
        <Box
          backgroundColor="surfaceTertiaryFill"
          paddingY={2}
          minH="100vh"
          paddingX={4}
        >
          <AddReviewerWithSearch
            closeModal={handleCloseModal}
            accountCreationEnabled={isReviewerAccountCreationEnabled}
            blockedReviewers={blockedReviewers}
          />
        </Box>
        <Flex
          padding={4}
          bottom={0}
          zIndex={99}
          justifyContent="center"
          position="sticky"
          bgColor="surfacePrimaryFill"
        >
          <Flex width="100%" justifyContent="space-between">
            <Button variant="caution" onClick={handleCloseModal}>
              Exit
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={() => setShowConfirmModal(true)}
              disabled={!confirmedReviewers.length || isProcessing}
              isLoading={isProcessing}
            >
              Add to queue
            </Button>
          </Flex>
        </Flex>
      </Overlay>
    </Overlay>
  );
};

export default AddReviewerToQueueModal;
